import { useEffect, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to track when units gain shields and play shield equip sound
 * Compares previous and current game state to detect new shield grants
 */
export const useShieldGrantAnimations = (gameState, userId) => {
  const previousShieldsRef = useRef(new Map());
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (!gameState?.state || !userId) return;
    
    // ✅ Skip detection on first render - we don't want to play sounds for shields already granted
    if (isFirstRenderRef.current) {
      console.log(`🛡️ First render - initializing shield tracking (skipping sound)`);
      isFirstRenderRef.current = false;
      // Still build the map for next comparison
      const currentShields = new Map();
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      allPlayerIds.forEach(playerId => {
        const player = gameState.state[playerId];
        if (!player?.playedOrders) return;
        ['front', 'flank'].forEach(zone => {
          if (player.playedOrders[zone]) {
            player.playedOrders[zone].forEach((unit, idx) => {
              if (unit && unit.instanceId) {
                const key = `${playerId}_${zone}_${idx}`;
                // Track if unit has shield
                currentShields.set(key, unit.hasShield === true);
              }
            });
          }
        });
      });
      previousShieldsRef.current = currentShields;
      return; // Don't process shield grants on first render
    }

    const currentShields = new Map();
    const allPlayerIds = gameState.players || Object.keys(gameState.state);

    // Build map of current shield states
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit, idx) => {
            if (unit && unit.instanceId) {
              const key = `${playerId}_${zone}_${idx}`;
              // Track if unit has shield
              currentShields.set(key, unit.hasShield === true);
            }
          });
        }
      });
    });

    // Detect newly granted shields
    const newShieldGrants = [];
    currentShields.forEach((hasShield, key) => {
      const previousHasShield = previousShieldsRef.current.get(key);
      
      // ✅ Detect when a unit gains a shield (didn't have one before, has one now)
      if (!previousHasShield && hasShield) {
        newShieldGrants.push(key);
        console.log(`🛡️ SHIELD GRANT DETECTED: ${key} - unit gained shield`);
      }
    });

    // ✅ Play shield equip sound for each newly granted shield
    if (newShieldGrants.length > 0) {
      newShieldGrants.forEach((key, idx) => {
        const [playerId, zone, indexStr] = key.split('_');
        const index = parseInt(indexStr);
        const player = gameState.state[playerId];
        
        if (player?.playedOrders?.[zone]?.[index]) {
          const unit = player.playedOrders[zone][index];
          
          // Small delay to stagger sounds if multiple shields are granted at once
          const delay = newShieldGrants.length > 1 ? idx * 100 : 0;
          setTimeout(() => {
            console.log(`🛡️ Playing shield equip sound for ${unit.name} (ID: ${unit.id}) at ${zone}[${index}]`);
            try {
              playSoundEffect('/sounds/shield_2.mp3', 0.5); // Play at 50% volume
              console.log(`✅ Shield equip sound triggered successfully`);
            } catch (error) {
              console.error(`❌ Error playing shield equip sound:`, error);
            }
          }, delay);
        } else {
          console.warn(`⚠️ SHIELD GRANT SOUND: Unit not found at ${zone}[${index}] for player ${playerId}`);
        }
      });
    }

    // Update previous shields reference
    previousShieldsRef.current = currentShields;
  }, [gameState, userId]);
};

